import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import Grid from "../grid"
import {
  Main,
  Aside,
  Content,
  ContentWrapper,
  ContentMessage,
  ContentSection,
  CurrentSectionTitle,
  CurrentSectionHeading,
  CurrentSectionHighlight,
  CurrentSectionHighlightBorder,
  CurrentSectionHighlightText,
  CurrentSectionChevron,
  CloseButton,
  JumpNav,
  JumpNavButton,
  Sidebar,
  SidebarActions,
  Buttons,
  MoreOptions,
  ActivityLogContainer,
  H1,
  ActivityLogs,
  ActivityLog,
  ActivityLogAvatar,
  ActivityLogContent,
  ActivityLogUserAction,
  ActivityLogTimestamp,
  ActivityLogInputContainer,
  StyledButton,
  DropdownMenu,
  DropdownItem,
  MoreOptionsIcon,
  StickyMobileFooter,
  StickyFooterButton,
  Counter,
  StyledDeleteButton,
  ActivityLogEmptyState,
  WhiteBgActivityLogTextArea
} from "./styles"
import Card from "../card"
import Nav from "../nav"
import Note from "../profile/note"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faList } from "@fortawesome/pro-solid-svg-icons"
import Avatar from "../avatar"
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons"
import { faPaperPlaneTop } from "@fortawesome/pro-solid-svg-icons"
import { faTrash } from "@fortawesome/pro-regular-svg-icons"
import Button from "../button"
import { MobileOnly } from "../profile/styles"
import { DesktopOnly } from "../profile-info/styles"

let scrollTimeout = null
const SpaContentNav = ({
  sections,
  sectionsMap,
  disclaimer,
  noteInfo,
  employer,
  openReference,
  toggleProfile,
  mobileScrollingBoxRef,
  mobileHeaderRef,
  close,
  portalType,
  onRequestInterview,
  onInterview,
  bookInterviewSelectedSkill,
  bookInterviewSection,
  setSelectedOuter,
  initialSection,
  avatarUrl,
  avatarExists,
  obfuscated,
  employerName,
  firstName,
  lastName,
  buttonValues,
  activityLogs,
  applicationContainerId,
  addComment,
  removeComment,
  headerComponent
}) => {
  const contentHeightOffset = applicationContainerId ? 132 : 252
  const sidebarHeightOffset = applicationContainerId ? 350 : 252
  const [selected, setSelected] = useState(sections[0].item_name)
  const [currentSectionTitle, setCurrentSectionTitle] = useState(sections[0].item_friendly_name)
  const [sectionTitleFixed, setSectionTitleFixed] = useState(false)
  const [navItemClicked, setNavItemClicked] = useState(false)
  const [jumpNavOpen, setJumpNavOpen] = useState(false)
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false)
  const contentBoxRef = useRef()
  const itemsRef = useRef(null)
  const scrollingOffset = 10
  const mobileScrollingOffset = 320
  const [comment, setComment] = useState("")

  // Ref for MoreOptions (applies to both desktop and mobile)
  const moreOptionsRef = useRef(null)
  // Ref for DropdownMenu (applies to both desktop and mobile)
  const dropdownMenuRef = useRef(null)
  const activityLogRef = useRef(null)

  const scrollToItem = (itemName, mobileScroll = false) => {
    const map = getMap()
    let node = map.get(itemName)

    if (!node && itemName == "roles") {
      node = document.querySelector(
        ".fieldset_employer_camp_roles_available_profile_fs, .fieldset_employer_resort_roles_available_profile_fs"
      )
    }

    if (!node) return

    let top
    let specialOffset = itemName == "roles" ? 36 : 0
    if (mobileScroll) {
      top = node.offsetTop + mobileScrollingOffset - specialOffset
      mobileScrollingBoxRef.current.scrollTo({ top: top, behavior: "smooth" })
    } else {
      top = node.offsetTop + scrollingOffset - specialOffset
      contentBoxRef.current.scrollTo({ top: top, behavior: "smooth" })
    }
  }

  const setSelectedContent = (itemName, clicked = true, callOuterCallback = true, mobileScroll = false) => {
    if (clicked) {
      setNavItemClicked(true)

      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        setNavItemClicked(false)
      }, 1000)

      scrollToItem(itemName, mobileScroll)
    } else {
      setSelected(itemName)
      if (callOuterCallback) setSelectedOuter(itemName)
      setCurrentSectionTitle(sections.find((x) => x.item_name === itemName).item_friendly_name)
    }
  }

  const getMap = () => {
    if (!itemsRef.current) itemsRef.current = new Map()

    return itemsRef.current
  }

  useEffect(() => {
    if (!mobileScrollingBoxRef?.current) return

    const watchScroll = () => {
      const headerHeight = mobileHeaderRef.current?.clientHeight
      const top = mobileScrollingBoxRef.current?.scrollTop

      setSectionTitleFixed(top > headerHeight)
    }

    mobileScrollingBoxRef.current?.addEventListener("scroll", watchScroll)
    return () => mobileScrollingBoxRef.current?.removeEventListener("scroll", watchScroll)
  }, [mobileScrollingBoxRef, mobileHeaderRef])

  useEffect(() => {
    if (initialSection) setSelectedContent(initialSection)
  }, [initialSection])

  useEffect(() => {
    const handleScroll = (ref) => {
      if (!ref) return
      if (navItemClicked) return

      const isVisible = function (ele, container) {
        const eleTop = ele.offsetTop
        const eleBottom = eleTop + ele.clientHeight

        const containerTop = container?.scrollTop || 0
        const containerBottom = containerTop + (container?.clientHeight || 0)

        // The element is fully visible in the container
        return (
          (eleTop >= containerTop && eleBottom <= containerBottom) ||
          // Some part of the element is visible in the container
          (eleTop < containerTop && containerTop < eleBottom) ||
          (eleTop < containerBottom && containerBottom < eleBottom)
        )
      }

      const isContained = (ele, container) => {
        const eleTop = ele.offsetTop - 90 // Adjust threshold as needed
        const eleBottom = eleTop + ele.clientHeight

        const scrollPosition = container?.scrollTop || 0
        return scrollPosition >= eleTop && scrollPosition < eleBottom
      }

      const reversedSections = sections.slice().reverse()
      let foundSection = false
      for (const section of reversedSections) {
        const sectionElement = document.getElementById(`section_${section.item_name}`)
        if (sectionElement) {
          // If last section, check for any intersection
          const isLastSection = reversedSections[0] === section

          if (isLastSection && isVisible(sectionElement, ref.current)) {
            setSelectedContent(section.item_name, false, true, false)
            foundSection = true
            break
          } else if (isContained(sectionElement, ref.current)) {
            setSelectedContent(section.item_name, false, true, false)
            foundSection = true
            break
          }
        }
      }

      // Check if Activity Log is in view if not found in sections
      if (!foundSection && activityLogRef.current && isVisible(activityLogRef.current, ref.current)) {
        setSelected("activity_log")
        setCurrentSectionTitle("Activity Log")
        setSelectedOuter("activity_log")
      }
    }

    contentBoxRef.current?.addEventListener("scroll", handleScroll.bind(this, contentBoxRef))
    mobileScrollingBoxRef.current?.addEventListener("scroll", handleScroll.bind(this, mobileScrollingBoxRef))
    return () => {
      contentBoxRef.current?.removeEventListener("scroll", handleScroll.bind(this, contentBoxRef))
      mobileScrollingBoxRef.current?.removeEventListener("scroll", handleScroll.bind(this, mobileScrollingBoxRef))
    }
  }, [sections, contentBoxRef, navItemClicked, mobileScrollingBoxRef])

  useEffect(() => {
    if (!moreOptionsOpen) return

    function handleClickOutside(event) {
      if (
        moreOptionsRef.current &&
        !moreOptionsRef.current.contains(event.target) &&
        (!dropdownMenuRef.current || !dropdownMenuRef.current.contains(event.target))
      ) {
        setMoreOptionsOpen(false)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [moreOptionsOpen])

  // Helper to select the main button and the rest for MoreOptions
  const getMainAndMoreOptionsButtons = (buttonValues) => {
    if (!buttonValues || buttonValues.length === 0) return { mainButton: null, moreOptionsButtons: [] }
    let mainButton = null

    if (!buttonValues[0].disabled) {
      mainButton = buttonValues[0]
    } else {
      // If the first button is disabled, search for an enabled button whose label contains 'download' (case-insensitive)
      mainButton = buttonValues.find(
        (btn) => typeof btn.label === "string" && btn.label.toLowerCase().includes("download") && !btn.disabled
      )
      // Otherwise use message button
      if (!mainButton) {
        mainButton = buttonValues.find(
          (btn) => typeof btn.label === "string" && btn.label.toLowerCase().includes("message") && !btn.disabled
        )
      }
      // Otherwise use the next enabled button
      if (!mainButton) {
        mainButton = buttonValues.slice(1).find((btn) => !btn.disabled)
      }
    }
    if (!mainButton) return { mainButton: null, moreOptionsButtons: [] }
    const moreOptionsButtons = buttonValues.filter((btn) => btn !== mainButton)
    return { mainButton, moreOptionsButtons }
  }

  // Handling activity log on mobile nav
  const isMobile = () => window.matchMedia && window.matchMedia("(max-width: 767px)").matches

  const navSections = isMobile()
    ? [
        ...sections.map((x) => ({ name: x.item_name, label: x.item_friendly_name })),
        ...(addComment !== undefined ? [{ name: "activity_log", label: "Activity Log" }] : [])
      ]
    : sections.map((x) => ({ name: x.item_name, label: x.item_friendly_name }))

  const jumpNavSections = isMobile()
    ? [
        ...sections.map((x) => ({ name: x.item_name, label: x.item_friendly_name })),
        ...(addComment !== undefined ? [{ name: "activity_log", label: "Activity Log" }] : [])
      ]
    : sections.map((x) => ({ name: x.item_name, label: x.item_friendly_name }))

  const handleNavSelect = (itemName, ...args) => {
    if (itemName === "activity_log") {
      if (activityLogRef.current) {
        activityLogRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      setSelected(itemName)
      setCurrentSectionTitle("Activity Log")
      setSelectedOuter(itemName)
      return
    }
    setSelectedContent(itemName, ...args)
  }

  return (
    <Main participantMode={!!applicationContainerId}>
      {applicationContainerId ? (
        <Sidebar>
          <>
            {avatarUrl !== undefined && (
              <Avatar
                avatarExists={avatarExists}
                src={avatarUrl}
                obfuscated={obfuscated}
                alt={employerName ? employerName : `${firstName} ${lastName}`}
                smallOnMobile={true}
                firstName={firstName}
                lastName={lastName}
                noBorder
                profile
              />
            )}
          </>
          {headerComponent && <MobileOnly>{headerComponent}</MobileOnly>}
          <DesktopOnly>
            <SidebarActions>
              <Buttons>
                {(() => {
                  const { mainButton } = getMainAndMoreOptionsButtons(buttonValues)
                  if (!mainButton) return null
                  return (
                    <Button
                      onClick={mainButton.onClick}
                      disabled={mainButton.disabled}
                      variant="primary"
                      size={mainButton.size}
                      outlined={mainButton.outlined}
                      fullwidth
                      fullHeight
                    >
                      {mainButton.label}
                    </Button>
                  )
                })()}
              </Buttons>
              {(() => {
                const { moreOptionsButtons } = getMainAndMoreOptionsButtons(buttonValues)
                if (moreOptionsButtons.length === 0) return null
                return (
                  <MoreOptions
                    ref={moreOptionsRef}
                    onClick={(e) => {
                      e.stopPropagation()
                      setMoreOptionsOpen((open) => !open)
                    }}
                  >
                    <MoreOptionsIcon>
                      <FontAwesomeIcon icon={faEllipsis} />
                    </MoreOptionsIcon>
                    {moreOptionsOpen && (
                      <DropdownMenu ref={dropdownMenuRef} onClick={(e) => e.stopPropagation()}>
                        {moreOptionsButtons.map((btn, idx) => (
                          <DropdownItem
                            key={idx}
                            type="button"
                            onClick={(e) => {
                              btn.onClick(e)
                              setMoreOptionsOpen(false)
                            }}
                            disabled={btn.disabled}
                            variant={btn.variant}
                          >
                            {btn.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    )}
                  </MoreOptions>
                )
              })()}
            </SidebarActions>
          </DesktopOnly>
          <Aside profile contentHeightOffset={sidebarHeightOffset} participantMode={true}>
            <Nav profile items={navSections} selected={selected} setSelected={handleNavSelect} />
          </Aside>
        </Sidebar>
      ) : (
        <Aside profile contentHeightOffset={contentHeightOffset}>
          <Nav profile items={navSections} selected={selected} setSelected={handleNavSelect} />
        </Aside>
      )}

      {currentSectionTitle && (
        <CurrentSectionTitle fixed={sectionTitleFixed}>
          <CurrentSectionHighlight>
            <CurrentSectionHighlightText onClick={() => setJumpNavOpen(!jumpNavOpen)}>
              <CurrentSectionHeading>{currentSectionTitle}</CurrentSectionHeading>
              <CurrentSectionChevron direction={jumpNavOpen ? "up" : "down"}>
                <FontAwesomeIcon icon={faChevronDown} />
              </CurrentSectionChevron>
            </CurrentSectionHighlightText>
            <CurrentSectionHighlightBorder />

            {jumpNavOpen && (
              <JumpNav>
                {jumpNavSections.map((item, index) => (
                  <JumpNavButton
                    key={index}
                    selected={selected === item.name}
                    onClick={() => {
                      if (item.name === "activity_log") {
                        if (activityLogRef.current) {
                          activityLogRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
                        }
                        setSelected(item.name)
                        setCurrentSectionTitle("Activity Log")
                        setSelectedOuter(item.name)
                        setJumpNavOpen(false)
                        return
                      }
                      setSelectedContent(item.name, true, false, true)
                      setJumpNavOpen(false)
                    }}
                  >
                    {item.label}
                  </JumpNavButton>
                ))}
              </JumpNav>
            )}
          </CurrentSectionHighlight>

          {sectionTitleFixed && (
            <CloseButton size="large" variant="content" onClick={close} participantMode={!!applicationContainerId} />
          )}
        </CurrentSectionTitle>
      )}

      <Content ref={contentBoxRef} contentHeightOffset={contentHeightOffset}>
        {disclaimer && (
          <Card variant="warning" marginless style={{ marginTop: "2rem" }}>
            {disclaimer}
          </Card>
        )}

        <Grid gap="0" style={{ justifyContent: "center" }}>
          {sections &&
            sections.map((section, i) => {
              const key = section.item_type.toLowerCase().includes("form") ? "form" : section.item_name

              let Section
              if (key == "form" && !section.fieldsets) {
                Section = null
              } else {
                Section = sectionsMap[key]
              }

              const skipBorder = i === sections.length - 1
              const sectionRef = React.createRef()

              return Section !== null ? (
                <ContentWrapper
                  key={`content_wrapper_${section.item_name}`}
                  skipBorder={skipBorder}
                  id={`section_${section.item_name}`}
                  ref={(node) => {
                    const mapper = getMap()
                    if (node) {
                      mapper.set(section.item_name, node)
                    } else {
                      mapper.delete(section.item_name)
                    }
                  }}
                  participantMode={!!applicationContainerId}
                >
                  <ContentSection ref={sectionRef}>
                    {i === 0 && noteInfo && noteInfo.notes && <Note {...noteInfo} />}

                    {section.item_introduction && section.item_introduction.length && (
                      <ContentMessage>{section.item_introduction}</ContentMessage>
                    )}

                    <Section
                      portalType={portalType}
                      employer={employer}
                      {...section}
                      openReference={openReference}
                      toggleProfile={toggleProfile}
                      onRequestInterview={onRequestInterview}
                      onInterview={onInterview}
                      bookInterviewSelectedSkill={bookInterviewSelectedSkill}
                      bookInterviewSection={bookInterviewSection}
                      initialSection={initialSection}
                    />
                  </ContentSection>
                </ContentWrapper>
              ) : null
            })}
        </Grid>
      </Content>

      {addComment && (
        <ActivityLogContainer ref={activityLogRef}>
          <H1>
            Activity Log {activityLogs && activityLogs.length > 0 ? <Counter>{activityLogs.length}</Counter> : ""}
          </H1>
          <ActivityLogInputContainer mobileOnly={true}>
            <WhiteBgActivityLogTextArea
              name="comment"
              value={comment}
              placeholder="Add a comment"
              handleInputChange={(name, value) => setComment(value)}
              showValidation={false}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && comment.trim()) {
                  e.preventDefault()
                  addComment(comment, applicationContainerId)
                  setComment("")
                }
              }}
              rows={1}
            />
            <StyledButton
              onClick={() => {
                addComment(comment, applicationContainerId)
                setComment("")
              }}
              disabled={!comment.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
            </StyledButton>
          </ActivityLogInputContainer>
          <ActivityLogs>
            {activityLogs && activityLogs.length > 0 ? (
              activityLogs.map((log, idx) => (
                <ActivityLog key={idx}>
                  <ActivityLogAvatar src={log.logged_by_user_avatar} alt="avatar" />
                  <ActivityLogContent>
                    <ActivityLogUserAction>{log.content}</ActivityLogUserAction>
                    <ActivityLogTimestamp>
                      {log.created_at}{" "}
                      {removeComment && log.content.includes("commented:") && (
                        <StyledDeleteButton onClick={() => removeComment(log, idx)} title="Delete comment">
                          <FontAwesomeIcon icon={faTrash} />
                        </StyledDeleteButton>
                      )}
                    </ActivityLogTimestamp>
                  </ActivityLogContent>
                </ActivityLog>
              ))
            ) : (
              <ActivityLogEmptyState>
                <FontAwesomeIcon icon={faList} style={{ fontSize: "32px", marginBottom: "12px", color: "#d3d3d3" }} />
                <div>No activity yet</div>
              </ActivityLogEmptyState>
            )}
          </ActivityLogs>
          <ActivityLogInputContainer desktopOnly={true}>
            <WhiteBgActivityLogTextArea
              name="comment"
              value={comment}
              placeholder="Add a comment"
              handleInputChange={(name, value) => setComment(value)}
              showValidation={false}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && comment.trim()) {
                  e.preventDefault()
                  addComment(comment, applicationContainerId)
                  setComment("")
                }
              }}
              rows={1}
            />
            <StyledButton
              onClick={() => {
                addComment(comment, applicationContainerId)
                setComment("")
              }}
              disabled={!comment.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
            </StyledButton>
          </ActivityLogInputContainer>
        </ActivityLogContainer>
      )}

      {(() => {
        const { mainButton, moreOptionsButtons } = getMainAndMoreOptionsButtons(buttonValues)
        if (!mainButton) return null
        return (
          <MobileOnly>
            <StickyMobileFooter>
              {moreOptionsButtons.length > 0 && (
                <MoreOptions
                  ref={moreOptionsRef}
                  onClick={(e) => {
                    e.stopPropagation()
                    setMoreOptionsOpen((open) => !open)
                  }}
                >
                  <MoreOptionsIcon>
                    <FontAwesomeIcon icon={faEllipsis} />
                  </MoreOptionsIcon>
                  {moreOptionsOpen && (
                    <DropdownMenu ref={dropdownMenuRef} onClick={(e) => e.stopPropagation()}>
                      {moreOptionsButtons.map((btn, idx) => (
                        <DropdownItem
                          key={idx}
                          type="button"
                          onClick={(e) => {
                            btn.onClick(e)
                            setMoreOptionsOpen(false)
                          }}
                          disabled={btn.disabled}
                          variant={btn.variant}
                        >
                          {btn.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </MoreOptions>
              )}
              <StickyFooterButton
                onClick={mainButton.onClick}
                disabled={mainButton.disabled}
                variant="primary"
                size={mainButton.size}
                outlined={mainButton.outlined}
                fullwidth
              >
                {mainButton.label}
              </StickyFooterButton>
            </StickyMobileFooter>
          </MobileOnly>
        )
      })()}
    </Main>
  )
}

SpaContentNav.propTypes = {
  sections: PropTypes.array.isRequired,
  sectionsMap: PropTypes.object.isRequired,
  portalType: PropTypes.string.isRequired,
  disclaimer: PropTypes.string,
  employer: PropTypes.object,
  openReference: PropTypes.func,
  toggleProfile: PropTypes.func,
  mobileScrollingBoxRef: PropTypes.object,
  mobileHeaderRef: PropTypes.object,
  close: PropTypes.func,
  bookInterviewSelectedSkill: PropTypes.string,
  bookInterviewSection: PropTypes.element,
  setSelectedOuter: PropTypes.func,
  contentHeightOffset: PropTypes.number,
  headerComponent: PropTypes.node,
  removeComment: PropTypes.func
}

SpaContentNav.defaultProps = {
  sections: [],
  sectionsMap: {},
  portalType: "",
  disclaimer: "",
  employer: {},
  openReference: null,
  toggleProfile: () => {},
  mobileScrollingBoxRef: {},
  mobileHeaderRef: {},
  close: () => {},
  bookInterviewSelectedSkill: null,
  bookInterviewSection: null,
  setSelectedOuter: () => {},
  contentHeightOffset: 252,
  headerComponent: null,
  removeComment: null
}

export default SpaContentNav
